import React, {
  FC, KeyboardEvent, useEffect, useState,
} from 'react';
import {
  Button, Table, TablePaginationConfig, Input, Checkbox,
} from 'antd';
import { FilterValue, SorterResult } from 'antd/lib/table/interface';
import { SearchOutlined } from '@ant-design/icons';
import { AxiosResponse } from 'axios';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import { Product } from '../../model/product';
import { Enquiry } from '../../model/enquiry';
import { ManualEnquiry } from '../../model/ManualEnquiry';
import { EvidenceProvider } from '../../model/EvidenceProvider';
import { GetEnquiryParams, overrideEnquiry } from '../../service/dao/EnquiryDao';
import EnquiryDetails from '../EnquiryDetails/EnquiryDetails';
import LinkButton from '../LinkButton';
import { getAttachedFile } from '../../service/FileService';
import { displayError, displayInfo, displaySuccess } from '../Message/Message';

import './EnquiryTable.less';

export const allStatuses: string[] = [
  'INSUFFICIENT_DETAILS',
  'PROCESSING',
  'REQUESTED',
  'CONFIRMED',
  'CANCELLED',
  'DNA_MISSED',
  'REPORT_RECEIVED',
  'ERROR',
  'CLOSED',
  'NO_RESPONSE',
  'PARTIAL',
];

const handleSearch = (
  close: boolean,
  selectedKeys: any,
  confirm: any,
  dataIndex: string,
  setSearchText: (text: string) => void,
  setSearchedColumn: (col: string) => void,
  event?: KeyboardEvent<HTMLInputElement>,
) => {
  confirm({ closeDropdown: close });
  setSearchText(selectedKeys[0]);
  setSearchedColumn(dataIndex);
  if (event) event.stopPropagation();
};

const handleReset = (clearFilters: any, setSearchText: (text: string) => void, setSearchedColumn: (col: string) => void) => {
  clearFilters();
  setSearchText('');
  setSearchedColumn('');
};

/**
 * Generate column filters for an EnquiryTable. To be used in the column definitions.
 *
 * @param title Heading of the column.
 * @param dataIndex Index of the column.
 * @param quickOptions An optional array of predefined values to display as checkboxes below the search input.
 * @param t An optional translation function from i18n.
 */
export const filterColumn = (title: string, dataIndex: string, quickOptions?: string[]) => {
  const [, setSearchText] = useState('');
  const [, setSearchedColumn] = useState('');

  const { t } = useTranslation();

  return ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }: any) => {

      const onCheckboxChange = (checkedValue: string) => {
        const newValue = selectedKeys[0] === checkedValue ? [] : [checkedValue];
        setSelectedKeys(newValue);
      };

      return (
        <div className="column-filter">
          <Input
            autoFocus
            placeholder={`${t('enquiry.table.filter.search')} ${title}`}
            value={selectedKeys[0]}
            onChange={(event) => setSelectedKeys(event.target.value ? [event.target.value] : [])}
            onPressEnter={(event) => handleSearch(true, selectedKeys, confirm, dataIndex, setSearchText, setSearchedColumn, event)}
          />
          {quickOptions && quickOptions.length > 0 && t && (
            <div className="quick-select">
              <span>{t(`enquiry.table.filter.quick.select`)}</span>
              {quickOptions.map((option) => (
                <Checkbox
                  key={option}
                  checked={selectedKeys[0] === option}
                  onChange={() => onCheckboxChange(option)}
                >
                  {option}
                </Checkbox>
              ))}
            </div>
          )}
          <br />
          <Button
            type="primary"
            onClick={() => handleSearch(true, selectedKeys, confirm, dataIndex, setSearchText, setSearchedColumn)}
            icon={<SearchOutlined />}
            size="small"
          >
            {t('enquiry.table.filter.search')}
          </Button>
          <Button
            onClick={() => clearFilters && handleReset(clearFilters, setSearchText, setSearchedColumn)}
            size="small"
          >
            {t('enquiry.table.filter.reset')}
          </Button>
          <Button
            type="link"
            onClick={() => handleSearch(false, selectedKeys, confirm, dataIndex, setSearchText, setSearchedColumn)}
            size="small"
          >
            {t('enquiry.table.filter.filter')}
          </Button>
        </div>
      )
    },
    filterIcon: (<SearchOutlined />),
    filterMultiple: false,
  });
};

const displayFile = (accessToken: string, enquiryId: string, fileName: string, t: TFunction, setBusy?: Function) => {
  if (setBusy) setBusy(true);
  getAttachedFile(accessToken, enquiryId, fileName, t)
    .then((file) => {
      if (file.url) window.open(file.url, '_blank');
      else if (file.info) displayInfo(file.info);
      else if (file.error) displayError(file.error);
      else displayError(t('enquiry.table.attachment.not.found'));
      if (setBusy) setBusy(false);
    });
};

/**
 * Generate the external links for the given enquiry.
  * @param accessToken Bearer token for the user's session.
  * @param enquiry The enquiry details.
 * @param t Translation function from i18n.
 * @param translationPrefix The prefix for file type (purpose) translations.
 * @param setBusy Function to set the busy flag while files are being loaded
 */
export const enquiryLinks = (accessToken: string, enquiry: Enquiry, t: TFunction, translationPrefix: string, setBusy?: Function) => {
  const links: React.JSX.Element[] = [];
  let index = 1;

  // There's always a history link
  links.push(
    <LinkButton
      key={index}
      title={enquiry.enquiryId}
      text={t(`${translationPrefix}history`)}
      url={`${enquiry.engineUrl}${enquiry.enquiryId}`}
      maxWidth={320}
    />,
  );

  // Attached files (if present)
  if (enquiry.attachedFiles) {
    enquiry.attachedFiles.forEach((file) => {
      const consentText = t(`${translationPrefix}${file.purpose}`);
      index += 1;
      links.push(
        <>
          <br />
          <LinkButton
            key={index}
            title={enquiry.enquiryId}
            text={consentText}
            onClick={() => displayFile(accessToken, enquiry.enquiryId, file.fileName, t, setBusy)}
            maxWidth={320}
          />
        </>,
      );
    });
  }

  return links;
};

/**
 * Display the booking status from all the medical exams associated with the enquiry.
 * @param evidenceProviders Details of the medical exams.
 */
export const renderBookingStatus = (evidenceProviders: EvidenceProvider[]) => {
  const dates: React.JSX.Element[] = [];
  let index = 1;

  if (evidenceProviders) {
    evidenceProviders.forEach((provider) => {
      dates.push(<div key={index}>{provider.bookingStatus}</div>);
      index += 1;
    });
  }

  return dates;
};

/**
 * Display the medical booking update timestamps from all the medical exams associated with the enquiry.
 * @param evidenceProviders Details of the medical exams.
 */
export const renderBookingStatusUpdated = (evidenceProviders: EvidenceProvider[]) => {
  const dates: React.JSX.Element[] = [];
  let index = 1;

  if (evidenceProviders) {
    evidenceProviders.forEach((provider) => {
      dates.push(<div key={index}>{provider.bookingStatusUpdated?.toLocaleDateString()}</div>);
      index += 1;
    });
  }

  return dates;
};

/**
 * Display the medical evidence requested from all the medical exams associated with the enquiry.
 * @param evidenceProviders Details of the medical exams.
 */
export const renderEvidenceRequested = (evidenceProviders: EvidenceProvider[]) => {
  const evidence: React.JSX.Element[] = [];
  let index = 1;

  if (evidenceProviders) {
    evidenceProviders.forEach((provider) => {
      evidence.push(<div key={index}>{provider.evidenceRequested}</div>);
      index += 1;
    });
  }

  return evidence;
};

/**
 * Display the medical providers from all the medical exams associated with the enquiry.
 * @param evidenceProviders Details of the medical exams.
 */
export const renderEvidenceProvider = (evidenceProviders: EvidenceProvider[]) => {
  const dates: React.JSX.Element[] = [];
  let index = 1;

  if (evidenceProviders) {
    evidenceProviders.forEach((provider) => {
      dates.push(<div key={index}>{provider.evidenceProvider}</div>);
      index += 1;
    });
  }

  return dates;
};

/**
 * Checks whether an enquiry row is expandable (enquiry not actioned and contains info to show in expanded view)
 * @param enquiry
 */
const isRowExpandable = (enquiry: Enquiry | ManualEnquiry) => {
  const isValueMeaningful = (value: string) => value && value !== "[REDACTED]";
  return !enquiry.actioned
    && (isValueMeaningful(enquiry.email) || isValueMeaningful(enquiry.advisorEmail));
}

/**
 * Display a paginated table of enquiries.
 * @param columns Displayed column definitions.
 * @param getEnquiries Function to retrieve the enquiries from the server.
 * @param expandable Set to <code>true</code> if rows can be expanded to show enquiry details.
 * @param accessToken Bearer token for the user's session.
 * @constructor
 */
const EnquiryTable: FC<{
  columns: any[],
  getEnquiries: (token: string, requestParams: GetEnquiryParams) => Promise<AxiosResponse<any>>,
  expandable?: boolean,
  accessToken: string,
}> = ({
  columns,
  getEnquiries,
  expandable = false,
  accessToken,
}) => {
  const [isLoading, setLoading] = useState<boolean>(true);
  const [enquiries, setEnquiries] = useState<Enquiry[]>();
  const [total, setTotal] = useState<number>();
  const [params, setParams] = useState<GetEnquiryParams>({
    page: 1, pageSize: 10, sortField: 'bookingStatusUpdated', sortOrder: 'descend',
  });
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);

  let showDetails = false;
  if (expandable) showDetails = true;

  const retrieveEnquiries = (token?: string) => {
    setLoading(true);
    if (token) {
      getEnquiries(token, params)
        .then((data) => {
          setEnquiries(data.data.data);
          setTotal(data.data.metadata.totalEnquiries);
          setLoading(false);
        })
        .catch(() => displayError('Error occurred while retrieving the enquiries'));
    }
  };

  useEffect(() => {
    retrieveEnquiries(accessToken);
  }, [accessToken, params]);

  const handleParamChange = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter: SorterResult<Enquiry> | SorterResult<Enquiry>[],
  ) => {
    const changeParams: GetEnquiryParams = {
      pageSize: pagination.pageSize || 10,
      page: pagination.current || 1,
    };
    if (!Array.isArray(sorter)) {
      changeParams.sortField = sorter.field as string;
      changeParams.sortOrder = sorter.order as string;
    }
    if (filters) {
      Object.keys(filters).forEach((column) => {
        const value = filters[column];
        if (value) {
          changeParams.filterField = column;
          changeParams.filterValue = value[0].toString();
        }
      });
    }
    setParams(changeParams);
  };

  const setEnquiryExpanded = (expanded: boolean, enquiry: Enquiry | ManualEnquiry) => {
    if (expanded) {
      setExpandedKeys([...expandedKeys, enquiry.enquiryId]);
    } else {
      setExpandedKeys(expandedKeys.filter((key) => key !== enquiry.enquiryId));
    }
  };

  const handleOverride = (enquiry: Enquiry | ManualEnquiry, products?: Product[], token?: string) => {
    setEnquiryExpanded(false, enquiry);
    if (products && token) {
      // eslint-disable-next-line no-param-reassign
      enquiry.actioned = true;
      overrideEnquiry(enquiry.enquiryId, enquiry.providerId, products, token)
        .then(
          () => {
            displaySuccess('Override successful');
          },
          (response) => {
            displayError(`${response.toString()} ${response.data}`);
            // eslint-disable-next-line no-param-reassign
            enquiry.actioned = false;
          },
        );
    }
  };

  const getExpandable = (canExpand: boolean): object => {
    if (canExpand) {
      return {
        expandRowByClick: true,
        expandedRowKeys: expandedKeys,
        onExpand: setEnquiryExpanded,
        rowExpandable: isRowExpandable,
        // eslint-disable-next-line react/no-unstable-nested-components
        expandedRowRender: (enquiry: Enquiry | ManualEnquiry, _: any, __: any, expanded: boolean) => expanded
          && (
            <EnquiryDetails
              baseEnquiry={enquiry}
              onOverride={(products) => handleOverride(enquiry, products, accessToken)}
            />
          ),
      };
    }
    return {};
  };

  return (
    <Table
      className="enquiry-table"
      scroll={{ x: true }}
      columns={columns}
      dataSource={enquiries}
      rowKey="enquiryId"
      loading={isLoading}
      pagination={{
        pageSize: params.pageSize,
        current: params.page,
        total,
        hideOnSinglePage: true,
      }}
      onChange={handleParamChange}
      expandable={getExpandable(showDetails)}
      rowClassName={(enquiry) => (enquiry.actioned ? 'actioned' : '')}
    />
  );
};

export default EnquiryTable;
